import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router";
import SwiperCore from "swiper";

import { freePointApi, merchantApi } from "@api";
import { ComponentFreePointDetail, ComponentPopUpConfirm } from "@components";
import { enumStatus, enumTypes, enumValidation, PATH_HOME } from "@configs";
import { IFreePoint } from "@interfaces";
import {
    selectAuth,
    selectConfigClient,
    setExternalTypes,
    setFreePointId,
    setLoading,
} from "@redux";
import CacheStore from "@Rocket-Innovation/components/store/CacheStore";
import UserStore from "@Rocket-Innovation/components/store/UserStore";
import { formatMessage, useNotify } from "@utils";
import { deduplicate } from "src/Utils/requestDeduper";

export const PageFreePoint = () => {
    //page hook
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { id } = useParams<{ id: string }>();
    const history = useHistory();
    const { error, success, warning } = useNotify();
    //page redux
    const { auth, userInfo } = useSelector(selectAuth);
    const configClient = useSelector(selectConfigClient);
    //page state
    const [freePoint, setFreePoint] = useState<IFreePoint>();
    const [currentImage, setCurrentImage] = useState<number>(enumValidation.NUMBER_ONE);
    const [showPopupConfirm, setShowPopupConfirm] = useState<boolean>(false);
    //page variable

    useEffect(() => {
        useFreePoint();
    }, []);

    const useFreePoint = async () => {
        try {
            dispatch(setLoading(true));
            if (id) {
                // const res = await merchantApi.getFreePointId(id);
                const res = await deduplicate(`free-point-${id}`, () =>
                    merchantApi.getFreePointId(id)
                );
                const data = res.data as IFreePoint;
                setFreePoint(data);
                const index = configClient.findIndex((item) => item.merchantId === data.merchantId);
                if (index < 0 || userInfo?.status === enumStatus.INACTIVE) {
                    dispatch(setExternalTypes({ type: enumTypes.FREE_POINT, id }));
                } else {
                    dispatch(setExternalTypes(null));
                }
            } else {
                history.push("/");
            }
        } catch (err: any) {
            if (err.response) {
                error(formatMessage(err.response));
            }
            history.push("/");
        } finally {
            dispatch(setLoading(false));
        }
    };

    const checkIsLogin = () => {
        if (!auth?.accessToken && !UserStore.getState().accessToken) {
            dispatch(setFreePointId(id));

            //Update in Rocket-Innovation's components
            CacheStore.setState({
                appendFields: {
                    ...CacheStore.getState().appendFields,
                    freepointId: id,
                },
            });

            warning(t("page.please_sign_up_to_collect_free_point"));
            return history.push(PATH_HOME);
        } else if (userInfo?.status === enumStatus.INACTIVE) {
            history.push(PATH_HOME);
        } else {
            if (freePoint?.isNewMemberOnly) {
                warning(t("page.for_new_member_only"));
                history.push(PATH_HOME);
            } else {
                return true;
            }
        }
        return false;
    };

    const handleRedeemedFreePoint = async () => {
        try {
            dispatch(setLoading(true));
            const res = await freePointApi.redeemedFreePoint(
                {
                    objectId: freePoint?._id || "",
                    status: enumStatus.ACTIVE,
                    type: enumTypes.FREE_POINT,
                },
                id
            );
            if (res) {
                success(formatMessage(res));
            }
            // dispatch(fetchUser());
            history.push(PATH_HOME);
        } catch (err: any) {
            if (err.response) {
                error(formatMessage(err.response));
            } else {
                error(t("message.received.fail"));
            }
        } finally {
            dispatch(setLoading(false));
        }
    };

    const handleClosePopupConfirm = () => {
        setShowPopupConfirm(false);
    };
    const handleShowPopupConfirm = () => {
        checkIsLogin();
        if (checkIsLogin()) {
            setShowPopupConfirm(true);
        }
    };

    const handleChangeImage = (swiper: SwiperCore) => {
        setCurrentImage(swiper.realIndex + 1);
    };

    return (
        <>
            {freePoint && (
                <ComponentFreePointDetail
                    freePoint={freePoint}
                    handleToggleConfirm={handleShowPopupConfirm}
                    handleChangeImage={handleChangeImage}
                    currentImage={currentImage}
                />
            )}

            {showPopupConfirm && freePoint && (
                <ComponentPopUpConfirm
                    handleCloseDetail={handleClosePopupConfirm}
                    data={freePoint as IFreePoint}
                    handleConfirm={handleRedeemedFreePoint}
                    cNote=""
                />
            )}
        </>
    );
};
